/**
 * Permission constants for the RMS system
 *
 * Permission format: resource:action
 * - Use hyphens for multi-word resources (e.g., work-orders, yard-check, api-keys)
 * - Use 'read' for viewing data, NOT 'view'
 * - Use hyphens in compound actions (e.g., manage-roles, submit-result)
 *
 * Special permissions:
 * - '*' grants all permissions (superadmin)
 * - 'resource:*' grants all actions on that resource
 */

export const PERMISSIONS = {
  // Admin permissions
  ADMIN_ACCESS: 'admin:access',
  ADMIN_READ: 'admin:read',
  ADMIN_MANAGE: 'admin:manage',
  ADMIN_MANAGE_ROLES: 'admin:manage-roles',
  ADMIN_MANAGE_SETTINGS: 'admin:manage-settings',
  ADMIN_READ_LOGS: 'admin:read-logs',

  // User management
  USERS_CREATE: 'users:create',
  USERS_READ: 'users:read',
  USERS_UPDATE: 'users:update',
  USERS_DELETE: 'users:delete',
  USERS_MANAGE_ROLES: 'users:manage-roles',

  // Role management
  ROLES_CREATE: 'roles:create',
  ROLES_READ: 'roles:read',
  ROLES_UPDATE: 'roles:update',
  ROLES_DELETE: 'roles:delete',
  ROLES_MANAGE_PERMISSIONS: 'roles:manage-permissions',

  // Request management
  REQUESTS_CREATE: 'requests:create',
  REQUESTS_READ: 'requests:read',
  REQUESTS_UPDATE: 'requests:update',
  REQUESTS_DELETE: 'requests:delete',
  REQUESTS_APPROVE: 'requests:approve',
  REQUESTS_ASSIGN: 'requests:assign',
  REQUESTS_PRINT: 'requests:print',
  REQUESTS_SUBMIT_RESULT: 'requests:submit-result',

  // Train operations
  TRAINS_CREATE: 'trains:create',
  TRAINS_READ: 'trains:read',
  TRAINS_UPDATE: 'trains:update',
  TRAINS_DELETE: 'trains:delete',
  TRAINS_DISPATCH: 'trains:dispatch',
  TRAINS_PRINT: 'trains:print',
  TRAINS_SEND_REPORT: 'trains:send-report',

  // Unit management
  UNITS_CREATE: 'units:create',
  UNITS_READ: 'units:read',
  UNITS_UPDATE: 'units:update',
  UNITS_DELETE: 'units:delete',
  UNITS_INSPECT: 'units:inspect',

  // Fuel management
  FUEL_CREATE: 'fuel:create',
  FUEL_READ: 'fuel:read',
  FUEL_UPDATE: 'fuel:update',
  FUEL_DELETE: 'fuel:delete',
  FUEL_APPROVE: 'fuel:approve',
  FUEL_EXPORT: 'fuel:export',

  // Equipment management
  EQUIPMENT_CREATE: 'equipment:create',
  EQUIPMENT_READ: 'equipment:read',
  EQUIPMENT_UPDATE: 'equipment:update',
  EQUIPMENT_DELETE: 'equipment:delete',

  // Bulletin management
  BULLETINS_CREATE: 'bulletins:create',
  BULLETINS_READ: 'bulletins:read',
  BULLETINS_UPDATE: 'bulletins:update',
  BULLETINS_DELETE: 'bulletins:delete',

  // Work orders
  WORK_ORDERS_CREATE: 'work-orders:create',
  WORK_ORDERS_READ: 'work-orders:read',
  WORK_ORDERS_UPDATE: 'work-orders:update',
  WORK_ORDERS_DELETE: 'work-orders:delete',
  WORK_ORDERS_ASSIGN: 'work-orders:assign',
  WORK_ORDERS_APPROVE: 'work-orders:approve',

  // Time tracking
  TIME_CREATE: 'time:create',
  TIME_READ: 'time:read',
  TIME_UPDATE: 'time:update',
  TIME_DELETE: 'time:delete',
  TIME_APPROVE: 'time:approve',

  // Staffing visibility
  STAFFING_READ_WHOS_IN: 'staffing:read-whos-in',
  STAFFING_READ_SHIFT_UTILIZATION: 'staffing:read-shift-utilization',

  // Billing and invoicing
  BILLING_CREATE: 'billing:create',
  BILLING_READ: 'billing:read',
  BILLING_UPDATE: 'billing:update',
  BILLING_DELETE: 'billing:delete',
  BILLING_APPROVE: 'billing:approve',

  // Invoices
  INVOICES_READ: 'invoices:read',
  INVOICES_CREATE: 'invoices:create',
  INVOICES_UPDATE: 'invoices:update',
  INVOICES_DELETE: 'invoices:delete',
  INVOICES_APPROVE: 'invoices:approve',
  INVOICES_EXPORT: 'invoices:export',

  // Inventory management
  INVENTORY_CREATE: 'inventory:create',
  INVENTORY_READ: 'inventory:read',
  INVENTORY_UPDATE: 'inventory:update',
  INVENTORY_DELETE: 'inventory:delete',
  INVENTORY_ADJUST: 'inventory:adjust',
  INVENTORY_MANAGE: 'inventory:manage',

  // Reports and analytics
  REPORTS_READ: 'reports:read',
  REPORTS_EXPORT: 'reports:export',
  REPORTS_MANAGE: 'reports:manage',
  ANALYTICS_READ: 'analytics:read',

  // System settings
  SETTINGS_READ: 'settings:read',
  SETTINGS_UPDATE: 'settings:update',

  // Automation settings
  AUTOMATION_SETTINGS_READ: 'automation-settings:read',
  AUTOMATION_SETTINGS_UPDATE: 'automation-settings:update',

  // Dispatch operations
  DISPATCH_READ: 'dispatch:read',
  DISPATCH_MANAGE: 'dispatch:manage',
  DISPATCH_ASSIGN: 'dispatch:assign',

  // Yard operations
  YARD_CHECK_CREATE: 'yard-check:create',
  YARD_CHECK_READ: 'yard-check:read',
  YARD_CHECK_UPDATE: 'yard-check:update',
  YARD_CHECK_DELETE: 'yard-check:delete',
  YARD_CHECK_SKIP_TIME: 'yard-check:skip-time',

  // Check editing
  CHECKS_EDIT_OWN: 'checks:edit-own',
  CHECKS_EDIT_ANY: 'checks:edit-any',
  CHECKS_EDIT_AFTER_COMPLETE: 'checks:edit-after-complete',
  CHECKS_VIEW_EDIT_HISTORY: 'checks:view-edit-history',

  // Cable management
  CABLES_CREATE: 'cables:create',
  CABLES_READ: 'cables:read',
  CABLES_UPDATE: 'cables:update',
  CABLES_DELETE: 'cables:delete',

  // Clip-on management
  CLIP_ONS_CREATE: 'clip-ons:create',
  CLIP_ONS_READ: 'clip-ons:read',
  CLIP_ONS_UPDATE: 'clip-ons:update',
  CLIP_ONS_DELETE: 'clip-ons:delete',

  // Clip-on field operations
  CLIP_ONS_FIELD_ATTACH: 'clip-ons-field:attach',
  CLIP_ONS_FIELD_DETACH: 'clip-ons-field:detach',

  // Power pack management
  POWER_PACKS_CREATE: 'power-packs:create',
  POWER_PACKS_READ: 'power-packs:read',
  POWER_PACKS_UPDATE: 'power-packs:update',
  POWER_PACKS_DELETE: 'power-packs:delete',

  // Undermount genset management
  UNDERMOUNTS_CREATE: 'undermounts:create',
  UNDERMOUNTS_READ: 'undermounts:read',
  UNDERMOUNTS_UPDATE: 'undermounts:update',
  UNDERMOUNTS_DELETE: 'undermounts:delete',

  // Genset management
  GENSETS_CREATE: 'gensets:create',
  GENSETS_READ: 'gensets:read',
  GENSETS_UPDATE: 'gensets:update',
  GENSETS_DELETE: 'gensets:delete',

  // Waybill management
  WAYBILLS_CREATE: 'waybills:create',
  WAYBILLS_READ: 'waybills:read',
  WAYBILLS_UPDATE: 'waybills:update',
  WAYBILLS_DELETE: 'waybills:delete',

  // API access
  API_ACCESS: 'api:access',
  API_ADMIN: 'api:admin',

  // API Key management
  API_KEYS_CREATE: 'api-keys:create',
  API_KEYS_READ: 'api-keys:read',
  API_KEYS_UPDATE: 'api-keys:update',
  API_KEYS_DELETE: 'api-keys:delete',

  // File management
  FILES_UPLOAD: 'files:upload',
  FILES_DOWNLOAD: 'files:download',
  FILES_DELETE: 'files:delete',

  // Dashboard devices
  DASHBOARD_DEVICES_READ: 'dashboard-devices:read',
  DASHBOARD_DEVICES_WRITE: 'dashboard-devices:write',

  // Dashboard stations
  DASHBOARD_STATIONS_READ: 'dashboard-stations:read',
  DASHBOARD_STATIONS_WRITE: 'dashboard-stations:write',

  // Tracking
  TRACKING_READ: 'tracking:read',
  TRACKING_MANAGE: 'tracking:manage',

  // Tasks
  TASKS_READ: 'tasks:read',
  TASKS_WRITE: 'tasks:write',
  TASKS_COMPLETE: 'tasks:complete',
  TASKS_ASSIGN: 'tasks:assign',

  // Activity feed
  ACTIVITY_FEED_READ_ALL: 'activity-feed:read-all',
  ACTIVITY_FEED_READ_OWN: 'activity-feed:read-own',
  ACTIVITY_FEED_EXPORT: 'activity-feed:export',
  ACTIVITY_FEED_STATS: 'activity-feed:stats',

  // Aliases for MainLayout compatibility
  DISPATCH_VIEW: 'dispatch:read',
  REPORTS_VIEW: 'reports:read',
} as const;

/**
 * Type for permission values
 */
export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

/**
 * Helper to get all permission values as an array
 */
export const ALL_PERMISSIONS = Object.values(PERMISSIONS);